import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { useToast } from 'components/ui/use-toast';
import { Question } from '../../types';
import { fetchStreamingQuestionSummary } from '../../services/api';
import { Button } from '../ui/button';
import { TextLinesShimmer } from '../ui/text-lines-shimmer';

interface CallbackSummaryQuestionProps {
  question: Question;
  textValue: string;
  setTextValue: (value: string) => void;
  isSubmitting?: boolean;
  flowRunId?: number | null;
}

const TEXTAREA_MIN_HEIGHT_PX = 128;
const TEXTAREA_MAX_HEIGHT_PX = 540;

export const CallbackSummaryQuestion: React.FC<CallbackSummaryQuestionProps> = ({
  question,
  textValue,
  setTextValue,
  isSubmitting = false,
  flowRunId,
}) => {
  const { addToast } = useToast();
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  // The text the AI last wrote here, so we can tell when the learner has edited it.
  const [generatedText, setGeneratedText] = useState<string | null>(null);
  const [isConfirmingReplace, setIsConfirmingReplace] = useState(false);
  // What a new draft replaced, so the learner can undo it.
  const [undoText, setUndoText] = useState<string | null>(null);
  const replacedTextRef = useRef<string | null>(null);
  const textareaId = `callback-summary-${useId().replace(/:/g, '')}`;
  const lastGeneratedQuestionRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const accumulatedTextRef = useRef('');
  const hardTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const getResolvedUserPrompt = useCallback(() => {
    const customUserPrompt = question.metadata?.custom_user_prompt;
    if (typeof customUserPrompt === 'string' && customUserPrompt.trim()) {
      return customUserPrompt;
    }
    const legacyTemplatePrompt = question.metadata?.template;
    if (typeof legacyTemplatePrompt === 'string' && legacyTemplatePrompt.trim()) {
      return legacyTemplatePrompt;
    }
    return '';
  }, [question.metadata?.custom_user_prompt, question.metadata?.template]);

  const resizeTextarea = useCallback(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }

    element.style.height = 'auto';
    const nextHeight = Math.min(
      Math.max(element.scrollHeight, TEXTAREA_MIN_HEIGHT_PX),
      TEXTAREA_MAX_HEIGHT_PX,
    );
    element.style.height = `${nextHeight}px`;
    element.style.overflowY = element.scrollHeight > TEXTAREA_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, []);

  const clearHardTimeout = useCallback(() => {
    if (hardTimeoutRef.current) {
      clearTimeout(hardTimeoutRef.current);
      hardTimeoutRef.current = null;
    }
  }, []);

  const restoreReplacedText = useCallback(() => {
    if (replacedTextRef.current != null) {
      setTextValue(replacedTextRef.current);
      replacedTextRef.current = null;
    }
  }, [setTextValue]);

  const startStream = useCallback(
    (questionId: number, textBeingReplaced: string | null = null) => {
      // Abort any in-flight stream
      abortControllerRef.current?.abort();
      replacedTextRef.current = textBeingReplaced;
      accumulatedTextRef.current = '';
      setTextValue('');
      setGenerationError(null);
      setIsLoadingSummary(true);
      setIsStreaming(false);

      const controller = fetchStreamingQuestionSummary(
        questionId,
        flowRunId ?? undefined,
        (chunk) => {
          // First chunk: switch from spinner to textarea
          if (!accumulatedTextRef.current) {
            setIsLoadingSummary(false);
            setIsStreaming(true);
          }
          accumulatedTextRef.current += chunk;
          setTextValue(accumulatedTextRef.current);
        },
        () => {
          // Done
          clearHardTimeout();
          setIsStreaming(false);
          setIsLoadingSummary(false);
          lastGeneratedQuestionRef.current = questionId;
          setGeneratedText(accumulatedTextRef.current);
          if (replacedTextRef.current != null) {
            setUndoText(replacedTextRef.current);
            replacedTextRef.current = null;
          }
        },
        (err) => {
          clearHardTimeout();
          setIsStreaming(false);
          setIsLoadingSummary(false);
          lastGeneratedQuestionRef.current = null;
          restoreReplacedText();
          setGenerationError("We couldn't generate your summary. Please try again.");
          addToast({
            variant: 'destructive',
            title: 'Summary generation failed',
            description: 'Your module context is saved and ready to retry.',
          });
          void err;
        },
        question.metadata?.custom_user_prompt,
        question.metadata?.custom_system_prompt,
      );

      abortControllerRef.current = controller;
      clearHardTimeout();
      hardTimeoutRef.current = setTimeout(() => {
        abortControllerRef.current?.abort();
        setIsStreaming(false);
        setIsLoadingSummary(false);
        lastGeneratedQuestionRef.current = null;
        restoreReplacedText();
        setGenerationError('Summary generation took too long. Please try again.');
        addToast({ variant: 'destructive', title: 'Summary generation timed out' });
      }, 55000);
    },
    [
      addToast,
      clearHardTimeout,
      flowRunId,
      question.metadata?.custom_system_prompt,
      question.metadata?.custom_user_prompt,
      restoreReplacedText,
      setTextValue,
    ],
  );

  // Auto-generate on mount when appropriate
  useEffect(() => {
    const rawQuestionId = question?.id;

    if (typeof rawQuestionId !== 'number') {
      lastGeneratedQuestionRef.current = null;
      return;
    }

    const questionId = rawQuestionId;

    if (textValue?.trim()) {
      lastGeneratedQuestionRef.current = questionId;
      return;
    }

    if (lastGeneratedQuestionRef.current === questionId) {
      return;
    }

    if (!getResolvedUserPrompt()) {
      setGenerationError('This summary is missing its required prompt configuration.');
      addToast({ variant: 'destructive', title: 'A user prompt is required for summary generation' });
      lastGeneratedQuestionRef.current = questionId;
      return;
    }

    startStream(questionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, getResolvedUserPrompt, startStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearHardTimeout();
      abortControllerRef.current?.abort();
    };
  }, [clearHardTimeout]);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [isLoadingSummary, resizeTextarea, textValue]);

  useLayoutEffect(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }

    let previousWidth = element.getBoundingClientRect().width;
    let resizeFrame: number | null = null;
    const resizeOnWidthChange = () => {
      const width = element.getBoundingClientRect().width;
      // Height changes also notify ResizeObserver. Only remeasure wrapping when
      // the available width changes, so our own height updates cannot loop.
      if (width !== previousWidth) {
        previousWidth = width;
        if (resizeFrame === null) {
          // Defer height writes outside observer delivery to avoid loop warnings.
          resizeFrame = window.requestAnimationFrame(() => {
            resizeFrame = null;
            resizeTextarea();
          });
        }
      }
    };

    const cancelPendingResize = () => {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
    };

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(resizeOnWidthChange);
      observer.observe(element);
      return () => {
        observer.disconnect();
        cancelPendingResize();
      };
    }

    window.addEventListener('resize', resizeOnWidthChange);
    return () => {
      window.removeEventListener('resize', resizeOnWidthChange);
      cancelPendingResize();
    };
  }, [isLoadingSummary, resizeTextarea]);

  const handleGenerateSummary = useCallback(() => {
    if (isLoadingSummary || isStreaming) {
      return;
    }

    const rawQuestionId = question?.id;
    if (typeof rawQuestionId !== 'number') {
      addToast({ variant: 'destructive', title: 'Unable to generate summary for this question' });
      return;
    }

    if (!getResolvedUserPrompt()) {
      setGenerationError('This summary is missing its required prompt configuration.');
      addToast({ variant: 'destructive', title: 'A user prompt is required for summary generation' });
      return;
    }

    setIsConfirmingReplace(false);
    setUndoText(null);
    startStream(rawQuestionId, textValue.trim() ? textValue : null);
  }, [
    addToast,
    getResolvedUserPrompt,
    isLoadingSummary,
    isStreaming,
    question.id,
    startStream,
    textValue,
  ]);

  const trimmedText = textValue.trim();
  const isEdited = generatedText != null && trimmedText !== generatedText.trim();
  // Saved text from an earlier visit may hold the learner's edits, so ask before replacing it too.
  const replacingNeedsConfirmation = trimmedText.length > 0 && (generatedText == null || isEdited);

  const requestNewDraft = () => {
    if (replacingNeedsConfirmation) {
      setIsConfirmingReplace(true);
      return;
    }
    handleGenerateSummary();
  };

  const undoNewDraft = () => {
    if (undoText == null) return;
    setTextValue(undoText);
    setUndoText(null);
  };

  if (isLoadingSummary) {
    return (
      <div className="w-full min-h-[180px] h-full">
        <TextLinesShimmer lineCount={6} className="w-full h-full min-h-[180px]" />
        <p className="mt-3 text-sm text-foundation-base-darkbase-dark-300">Drafting from your earlier answers…</p>
      </div>
    );
  }

  const canRetry = Boolean(generationError && getResolvedUserPrompt());
  const actionsDisabled = isSubmitting || isLoadingSummary || isStreaming;

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={textareaId} className="text-sm font-semibold text-foundation-base-darkbase-dark-500">
          Your draft{isEdited ? ' · edited' : ''}
        </label>
        {isStreaming ? (
          <span className="text-sm text-foundation-base-darkbase-dark-300">Writing…</span>
        ) : !canRetry && getResolvedUserPrompt() ? (
          <button
            type="button"
            onClick={requestNewDraft}
            disabled={actionsDisabled}
            className="rounded-md text-sm font-semibold text-foundation-base-darkbase-dark-500 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span aria-hidden="true">↻ </span>New draft
          </button>
        ) : null}
      </div>
      <textarea
        id={textareaId}
        ref={textareaRef}
        value={textValue}
        onChange={(event) => setTextValue(event.target.value)}
        onInput={resizeTextarea}
        style={{ height: `${TEXTAREA_MIN_HEIGHT_PX}px` }}
        placeholder="Write your summary here"
        className="relative self-stretch w-full min-h-[128px] max-h-[700px] rounded-xl border border-foundation-base-darkbase-dark-100 bg-white px-4 py-3 shadow-sm resize-none overflow-y-auto text-base leading-6 text-foundation-base-darkbase-dark-500 placeholder:text-foundation-base-darkbase-dark-300 focus-visible:border-foundation-base-darkbase-dark-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={isSubmitting || isStreaming}
      />
      {isConfirmingReplace ? (
        <div
          role="alertdialog"
          aria-label="Replace your edits?"
          className="rounded-xl bg-white px-4 py-3 shadow-elevation-sm"
        >
          <p className="type-body-m text-foundation-base-darkbase-dark-500">
            A new draft replaces your edits. You can undo it after.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="auto" onClick={handleGenerateSummary} disabled={actionsDisabled}>
              Write a new draft
            </Button>
            <Button type="button" size="auto" variant="outline" onClick={() => setIsConfirmingReplace(false)}>
              Keep my edits
            </Button>
          </div>
        </div>
      ) : null}
      {undoText != null && !isStreaming && !isConfirmingReplace ? (
        <p className="text-sm text-foundation-base-darkbase-dark-300">
          New draft written.{' '}
          <button
            type="button"
            onClick={undoNewDraft}
            className="rounded-md font-semibold text-foundation-base-darkbase-dark-500 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2"
          >
            Undo
          </button>
        </p>
      ) : null}
      {generationError ? (
        <p
          role="alert"
          className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {generationError}
        </p>
      ) : null}
      {canRetry ? (
        <div className="flex justify-end">
          <Button size='auto' onClick={handleGenerateSummary} disabled={actionsDisabled}>
            Retry Summary
          </Button>
        </div>
      ) : null}
    </div>
  );
};
